import type { FC } from 'react';

import type { BADGE_PRESETS } from '@shared/dream-components';

import { Tooltip } from '~/components/Tooltip';
import type { CommunityBadge as CommunityBadgeRecord } from '~/interfaces/community';

import { Badge } from './Badge';

type PresetHex = (typeof BADGE_PRESETS)[number]['background_color'];

const PRESET_CLASSES: Record<PresetHex, string> = {
  '#EDE9FE': 'bg-violet-100 text-violet-800 group-data-[open]:bg-violet-200',
  '#E0E7FF': 'bg-indigo-100 text-indigo-800 group-data-[open]:bg-indigo-200',
  '#D1FAE5': 'bg-emerald-100 text-emerald-800 group-data-[open]:bg-emerald-200',
  '#FEF3C7': 'bg-amber-100 text-amber-800 group-data-[open]:bg-amber-200',
  '#FCE7F3': 'bg-pink-100 text-pink-800 group-data-[open]:bg-pink-200',
};

const isPresetHex = (hex: string): hex is PresetHex => hex in PRESET_CLASSES;

type CommunityBadgeProps = {
  badge: CommunityBadgeRecord;
  interactive?: boolean;
};

export const CommunityBadge: FC<CommunityBadgeProps> = ({ badge, interactive = true }) => {
  const hex = badge.background_color.toUpperCase();
  const presetClass = isPresetHex(hex) ? PRESET_CLASSES[hex] : undefined;

  const pill = (
    <Badge
      label={badge.name}
      className={presetClass}
      style={
        presetClass ? undefined : { backgroundColor: badge.background_color, color: badge.text_on_background_color }
      }
    />
  );

  return !interactive ? (
    <span title={`${badge.name}: ${badge.description}`} className="inline-flex items-center leading-none">
      {pill}
    </span>
  ) : (
    <Tooltip
      content={
        <span className="block max-w-[240px]">
          <span className="block font-semibold">{badge.name}</span>
          <span className="block">{badge.description}</span>
        </span>
      }
      placement="bottom"
      className="group inline-flex items-center"
      interactive
      arrow
    >
      <button
        type="button"
        aria-label={`${badge.name}: ${badge.description}`}
        className="inline-flex items-center p-0 m-0 border-0 bg-transparent leading-none cursor-default"
      >
        {pill}
      </button>
    </Tooltip>
  );
};
